import { useCallback, useContext, useEffect, useRef, useState } from "react";

import SaeDao from "@dao/SaeDao";

import { DASH_LINKS } from "@const/AppCardLinks";
import { formatDate, getEpoch, getTodayWithTime } from "@utils/DateFormatter";
import { GlobalContext } from "@components/contextProvider/ContextProvider";
import { isWindowOpen } from "@utils/ActionControlWindows";

import type { AppButton, AppObject } from "@/types/GlobalState";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SaeActionControl {
    action: string;
    startDate: string;
    endDate: string;
    [key: string]: unknown;
}

interface SchoolYearDto {
    key: string | number;
}

interface AeStudent {
    key?: string;
    studentNumber?: string | number;
    gradeName?: string | number;
    [key: string]: unknown;
}

interface OptOutDto {
    optOutDate?: string;
    [key: string]: unknown;
}

// ─── Module-level helpers ─────────────────────────────────────────────────────

const getButtons = (
    requestWindow: SaeActionControl,
    studentNumber: string | number | undefined
): AppButton[] | null => {
    if (!isWindowOpen(requestWindow.startDate, requestWindow.endDate)) return null;
    return [{ label: "AEApp_Btn1", href: `${DASH_LINKS.assessment_excusals}/${studentNumber}` }];
};

const getSaeRequestWindow = (
    saeActionControls: SaeActionControl[],
    _studentNumber: string | number | undefined
): SaeActionControl | null => {
    try {
        const controlMatch = saeActionControls.filter((obj) => obj.action === "PARENT_REQUEST");
        return controlMatch.length > 0 ? controlMatch[0] : null;
    } catch (error: unknown) {
        console.error("Unable to find SAE Action Controls for School Year", error);
        return null;
    }
};

const fetchAssessmentExcusalApp = async (
    requestWindow: SaeActionControl,
    schoolYearKey: string | number,
    studentNumber: string | number
): Promise<AppObject | null> => {
    try {
        const response = await SaeDao<{ payload: OptOutDto[] }>({
            action: "saeOptOutsByStudentRead",
            schoolYearKey,
            studentNumber
        });
        if (!response?.data?.payload) return null;

        const { payload } = response.data;
        const saeStartEpoch = getEpoch(requestWindow.startDate);
        const todayEpoch = getEpoch(getTodayWithTime());
        const notice =
            saeStartEpoch !== null && todayEpoch !== null && todayEpoch < saeStartEpoch
                ? [
                      "Global_Notice_Ae",
                      formatDate(requestWindow.startDate, false, true) ?? "",
                      "Global_Notice_Check_Back"
                  ]
                : null;

        return {
            app: "ASSESSMENT_EXCUSALS",
            body: "AEApp_Body",
            buttons: getButtons(requestWindow, studentNumber),
            endDate: formatDate(requestWindow.endDate, true, true),
            icon: "ASSESSMENT_EXCUSAL_ICON",
            moreInfo: null,
            notice,
            startDate: formatDate(requestWindow.startDate, true, true),
            status: payload.length > 0 ? "AEApp_MoreExcusals" : "AEApp_NoExcusals",
            statusColor: payload.length > 0 ? "#0669B3" : "#707070",
            statusNumber: payload.length > 0 ? payload.length : null,
            subBody: null,
            subStatus: payload.length > 0 ? "Global_Completed" : null,
            subStatusRange: payload.length > 0 ? formatDate(payload[0].optOutDate ?? null, true, true) : null,
            title: "AEApp_Header"
        };
    } catch (error: unknown) {
        console.error("Unable to retrieve Assessment Excusals App for student", error);
        return null;
    }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Custom hook to determine if a student's guardian has access to the Assessment Excusal app tile
 * @name useAeApp
 */
export const useAeApp = (student: AeStudent, enabled = true): { appObj: AppObject | null; loading: boolean } => {
    const context = useContext(GlobalContext);
    const { dispatch, state } = context ?? {};
    const { saeActionControls, schoolYearDto, studentAppObjects } =
        (state as {
            saeActionControls?: SaeActionControl[];
            schoolYearDto?: SchoolYearDto;
            studentAppObjects?: Record<string, Record<string, { appObj: AppObject | null }>>;
        }) || {};

    const [appObj, setAppObj] = useState<AppObject | null>(null);
    const [loading, setLoading] = useState(true);

    const studentKey = student?.key;
    const existing = studentKey ? studentAppObjects?.[studentKey]?.ASSESSMENT_EXCUSALS : null;

    const studentAppObjectsRef = useRef(studentAppObjects);
    useEffect(() => {
        studentAppObjectsRef.current = studentAppObjects;
    }, [studentAppObjects]);

    const dispatchAppObject = useCallback(
        (obj: AppObject | null) => {
            const current = studentAppObjectsRef.current;
            const payload = {
                ...current,
                [studentKey!]: {
                    ...current?.[studentKey!],
                    ASSESSMENT_EXCUSALS: { appObj: obj }
                }
            };
            dispatch?.({ type: "StudentAppObjects", studentAppObjects: payload });
        },
        [dispatch, studentKey]
    );

    useEffect(() => {
        if (!enabled) setLoading(false);
    }, [enabled]);

    useEffect(() => {
        if (existing) {
            setAppObj(existing.appObj);
            setLoading(false);
        }
    }, [existing]);

    useEffect(() => {
        if (existing) return;

        if (
            !saeActionControls ||
            !schoolYearDto ||
            !student?.studentNumber ||
            ["THIRTEENTH", 13, "13"].includes(student?.gradeName as string)
        ) {
            setAppObj(null);
            setLoading(false);
            return;
        }

        const run = async (): Promise<void> => {
            try {
                setLoading(true);
                const requestWindow = getSaeRequestWindow(saeActionControls, student.studentNumber);
                const app = requestWindow
                    ? await fetchAssessmentExcusalApp(requestWindow, schoolYearDto.key, student.studentNumber!)
                    : null;
                setAppObj(app);
                dispatchAppObject(app);
            } catch (error: unknown) {
                console.error("Unable to retrieve Assessment Excusals details for student", error);
            } finally {
                setLoading(false);
            }
        };

        if (enabled) void run();
    }, [dispatchAppObject, enabled, existing, saeActionControls, student, schoolYearDto]);

    return { appObj, loading };
};
